import CustomComponent from "../../components/customComponent";
import { withToast } from "../../App";
import hoistStatics from "hoist-non-react-statics";
import { withTranslation } from "react-i18next";
import { withRouter } from "../../components/withRouter";
import { BoaterLayout } from "../../components/Layouts";
import Dropdown from "../../components/Dropdown";
import LightBulbIconMulticolors from "../../assets/img/icons/lightbulb.svg";
import { withBoaterStorage } from "../../hooks/useBoaterStorage";
import { StepNavigation } from "../../components/StepNavigation";
import ChargingControlModal from "../../components/ChargingControlModal";

class BoaterProcessChargingControl extends CustomComponent {
    constructor(props) {
        super(props);
        const { boaterStorage } = props;

        this.state = {
            terminal: null,
            terminalConnectors: null,
            terminals: [],
            search: "",
            userPosition: null,
            isLoading: false,
            isTerminalModalOpen: false,
            modalPage: 1,
            isToggleOn: props.chargeAction === "stop",
            connectorId: boaterStorage.connectorId || null,
            connectorLabel: boaterStorage.connectorLabel || null,
            manualUrl: boaterStorage.manualUrl || null,
        };

        this.boundRequest = this.request.bind(this);
    }

    onModalClose = (keepFocus = false) => {
        this.setState({
            ...(!keepFocus && { terminal: null }),
            isTerminalModalOpen: false,
        });
    };

    handleToggle = (state) => {
        this.setState({ isToggleOn: state });
    };

    pollForNewTransaction = async () => {
        const { boaterStorage } = this.props;
        const maxAttempts = 30;
        const pollingInterval = 1500;
        let attempts = 0;
        const connectorId = this.state.connectorId;

        const energyType = boaterStorage.currentEnergyType || "Electricity";

        return new Promise((resolve) => {
            const checkForTransaction = async () => {
                try {
                    const data = await this.request("/transactions/", "GET");

                    if (data && data.status === "ok" && data.transactions) {
                        const transaction = data.transactions.find(
                            (t) => t.connectorId === connectorId,
                        );

                        if (transaction) {
                            resolve({
                                found: true,
                                transaction,
                                energyType,
                            });
                            return;
                        }
                    }

                    attempts++;
                    if (attempts >= maxAttempts) {
                        resolve({ found: false });
                        return;
                    }

                    setTimeout(checkForTransaction, pollingInterval);
                } catch (error) {
                    console.error("Error polling for transaction:", error);
                    resolve({ found: false });
                }
            };
            checkForTransaction();
        });
    };

    pollForTransactionStop = async (transactionId) => {
        const maxAttempts = 30;
        const pollingInterval = 1500;
        let attempts = 0;

        return new Promise((resolve) => {
            const checkTransactionStatus = async () => {
                try {
                    const data = await this.request(
                        `/transactions/${transactionId}`,
                        "GET",
                    );

                    if (
                        data &&
                        data.status === "ok" &&
                        data.transaction &&
                        (data.transaction.status === "Stopping" ||
                            data.transaction.status === "Finished")
                    ) {
                        resolve(true);
                        return;
                    }

                    attempts++;
                    if (attempts >= maxAttempts) {
                        resolve(false);
                        return;
                    }

                    setTimeout(checkTransactionStatus, pollingInterval);
                } catch (error) {
                    console.error("Error polling for transaction stop:", error);
                    resolve(false);
                }
            };
            checkTransactionStatus();
        });
    };

    handleNext = async () => {
        const { t, chargeAction, boaterStorage } = this.props;

        this.setState({ isLoading: true });

        if (chargeAction === "start" || chargeAction === "stop") {
            const url = `/terminal/charge/${chargeAction}/${this.state.connectorId}`;
            const data = await this.request(
                url,
                "POST",
                "",
                false,
                "/terminal/csrf",
            );
            if (data && data.status && data.status === "ok") {
                // For start action, poll for the transaction and redirect to its page
                if (chargeAction === "start") {
                    const result = await this.pollForNewTransaction();
                    this.setState({ isLoading: false });
                    if (result.found) {
                        this.props.addToast(t("charge_started"), {
                            appearance: "success",
                            autoDismiss: true,
                        });
                        this.props.navigate(
                            `/boater/transaction/${result.energyType}/${result.transaction.id}`,
                        );
                    } else {
                        this.props.addToast(t("charge_start_error"), {
                            appearance: "error",
                        });
                    }
                } else {
                    // For stop action, poll until transaction is no longer in list of transactions
                    const transactionId = boaterStorage.currentTransactionId;
                    const transactionType =
                        boaterStorage.currentTransactionType;

                    if (transactionId && transactionType) {
                        const result =
                            await this.pollForTransactionStop(transactionId);
                        this.setState({ isLoading: false });
                        if (result) {
                            this.props.addToast(t("charge_stopped"), {
                                appearance: "success",
                                autoDismiss: true,
                            });
                            this.props.navigate(
                                `/boater/transaction/${transactionType}/${transactionId}`,
                            );
                        } else {
                            this.props.addToast(t("charge_stop_error"), {
                                appearance: "error",
                            });
                        }
                    } else {
                        this.setState({ isLoading: false });
                        this.props.navigate("/boater");
                    }
                }
            }
        } else {
            this.props.navigate("/boater/step2");
        }
    };

    handleStepNext = () => {
        const { chargeAction } = this.props;

        if (chargeAction === "connect") {
            this.setState({
                isTerminalModalOpen: true,
            });
        } else {
            this.handleNext();
        }
    };

    handlePrevious = () => {
        const { chargeAction, boaterStorage } = this.props;

        if (chargeAction === "start") {
            this.props.navigate("/boater/payment");
        } else if (chargeAction === "stop") {
            const transactionType = boaterStorage.currentTransactionType;
            const transactionId = boaterStorage.currentTransactionId;

            if (transactionType && transactionId) {
                this.props.navigate(
                    `/boater/transaction/${transactionType}/${transactionId}`,
                );
            } else {
                this.props.navigate("/boater");
            }
        } else {
            this.props.updateBoaterStorage({ boater_return_to_modal: true });
            this.props.navigate("/boater/step1");
        }
    };

    openManual = () => {
        const { t } = this.props;

        if (this.state.manualUrl && this.state.manualUrl.length > 0) {
            try {
                window.open(this.state.manualUrl, "_blank").focus();
            } catch {
                this.props.addToast(t("blocked_file_popup"), {
                    appearance: "warning",
                });
            }
        } else {
            this.props.addToast(t("no_manual_available"), {
                appearance: "warning",
                autoDismiss: true,
            });
        }
    };

    render() {
        const { t, chargeAction, boaterStorage } = this.props;
        const { isToggleOn, isTerminalModalOpen, connectorLabel } = this.state;
        const terminalName = boaterStorage.terminalName;

        const step = chargeAction === "connect" ? 1 : 4;

        let headingText;
        if (chargeAction === "start") {
            headingText = t("boat_start_charge");
        } else if (chargeAction === "stop") {
            headingText = t("boat_stop_charge");
        } else {
            headingText = t("boat_connect");
        }

        const showModalInThisComponent = chargeAction === "connect";

        return (
            <BoaterLayout
                step={step}
                blueBackground
                terminalName={terminalName}
                loading={this.state.isLoading}
            >
                <div className="boater__step-container">
                    <div className="boater__terminal-connection-container">
                        <div className="boater__terminal-connection">
                            <img
                                src={LightBulbIconMulticolors}
                                width="69"
                                height="90"
                                alt=""
                            />
                            <div className="boater__terminal-connection-content">
                                <h2>{headingText}</h2>
                                <p>{t("terminal_instruction")}</p>
                                <Dropdown buttonText={t("details")}>
                                    <div>
                                        <button
                                            className="vnf-btn btn-download"
                                            onClick={this.openManual}
                                        >
                                            {t("terminal_manual")}
                                        </button>
                                    </div>
                                </Dropdown>
                            </div>
                        </div>
                    </div>

                    {showModalInThisComponent && (
                        <ChargingControlModal
                            isOpen={isTerminalModalOpen}
                            onClose={() => this.onModalClose()}
                            chargeAction={chargeAction}
                            isToggleOn={isToggleOn}
                            onToggle={this.handleToggle}
                            onNext={this.handleNext}
                            connectorLabel={connectorLabel}
                            connectorStatus="Available"
                            t={t}
                        />
                    )}

                    <StepNavigation
                        nextHandler={this.handleStepNext}
                        prevHandler={this.handlePrevious}
                        nextLabel={t("Next")}
                        prevTitle={t("Previous")}
                        nextTitle={t("Next")}
                    />
                </div>
            </BoaterLayout>
        );
    }
}

export default withToast(
    hoistStatics(
        withTranslation()(
            withRouter(withBoaterStorage(BoaterProcessChargingControl)),
        ),
        BoaterProcessChargingControl,
    ),
);
